import type { IObservableArray } from 'mobx';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { Button, FilePicker, Modal, Spinner, Text } from '@feathr/components';

import CreativeModal from '../CreativeModal';
import type { TChannel } from '../types';
import UploadResultsList from '../UploadResultsList';
import { useAddImageVideoFlow } from './AddImageVideoFlow.hooks';
import { getChannelConfig } from './AddImageVideoFlow.utils';

import * as styles from './AddImageVideoFlow.css';

export interface IAddImageVideoFlowProps {
  /** The channel type (determines validation rules and FilePicker preset) */
  channel: TChannel;
  /** Campaign ID to associate creatives with */
  campaignId: string;
  /** Observable array of creatives for optimistic updates */
  creatives?: IObservableArray<DisplayCreative>;
  /** Callback to trigger appear animation after adding to list */
  onAppear?: (id: string) => void;
  /** Callback when the entire flow completes (modal closed) */
  onComplete: () => void;
  /** Callback when user clicks Back from upload step */
  onBack: () => void;
  /** Callback when user clicks Cancel */
  onCancel: () => void;
}

/**
 * Unified flow for adding image and video creatives.
 * Supports both Meta and Display channels with channel-specific validation.
 *
 * Renders an inline Filestack picker for uploading, then opens the CreativeModal
 * for configuring the uploaded creatives.
 */
function AddImageVideoFlow({
  channel,
  campaignId,
  creatives,
  onAppear,
  onComplete,
  onBack,
  onCancel,
}: Readonly<IAddImageVideoFlowProps>): JSX.Element {
  const { t } = useTranslation();

  const {
    step,
    uploadState,
    isSaving,
    selectedCreative,
    thumbnailItems,
    uploadResults,
    handleUploadingChange,
    handleFilesUploaded,
    handleSelectCreative,
    handleContinue,
    handleSave,
    handleBack,
    handleClose,
  } = useAddImageVideoFlow({
    channel,
    campaignId,
    creatives,
    onAppear,
    onComplete,
    onBack,
    onCancel,
  });

  const config = getChannelConfig(channel, t);
  const hasSuccesses = uploadResults.some((r) => r.success);
  const hasResults = uploadResults.length > 0;
  const isProcessing = uploadState === 'processing';

  // Render upload step in a Modal
  if (step === 'upload') {
    const leftActions = (
      <Button key={'back'} name={'back'} onClick={handleBack}>
        {t('Back')}
      </Button>
    );

    const rightActions = [
      <Button key={'cancel'} name={'cancel'} onClick={handleClose}>
        {t('Cancel')}
      </Button>,
      <Button
        disabled={!hasSuccesses || uploadState !== 'idle'}
        key={'next'}
        name={'next'}
        onClick={handleContinue}
        type={'primary'}
      >
        {t('Next')}
      </Button>,
    ];

    return (
      <Modal
        description={config.description}
        leftActions={leftActions}
        onClose={handleClose}
        opened={true}
        rightActions={rightActions}
        size={'md'}
        title={t('Add image/video creative')}
      >
        <div className={styles.container}>
          <FilePicker
            displayMode={'inline'}
            onUpload={handleFilesUploaded}
            onUploadingChange={handleUploadingChange}
            preset={config.preset}
          />
          {isProcessing && (
            <div className={styles.processingOverlay}>
              <Spinner size={40} />
              <Text.DefaultRegular>{t('Processing uploaded files...')}</Text.DefaultRegular>
            </div>
          )}
          {hasResults && <UploadResultsList results={uploadResults} />}
        </div>
      </Modal>
    );
  }

  // Render creative modal step
  return (
    <CreativeModal
      creative={selectedCreative}
      isSaving={isSaving}
      onClose={handleClose}
      onSave={handleSave}
      onSelectCreative={handleSelectCreative}
      opened={true}
      skipValidationFields={config.skipValidationFields}
      thumbnailItems={thumbnailItems}
    />
  );
}

export default AddImageVideoFlow;
